import { datadogRum } from '@datadog/browser-rum';
import type { MaterializedMenuProduct } from '@iheartjane/data-access';
import type { SmartSort, IntendedDisplay } from '@iheartjane/dm-sdk';
import { FLAGS } from '@iheartjane/feature-flags';
import type {
  BannerCarouselSlide,
  BannerCustomImageAdSlide,
  WebCustomImageAdEvent,
} from '@iheartjane/menu';
import {
  BannerCarousel,
  imageAdTrackingPayload,
  useIsModalOpen,
} from '@iheartjane/menu';
import { useCallback, useMemo, useState } from 'react';

import { MENU_RESULTS_ELEMENT_ID } from '@jane/bloom/components/productListingPage/productGrid';
import { CarouselSkeleton } from '@jane/bloom/components/skeletonLoaders/carouselSkeleton';
import type { CarouselBanner } from '@jane/bloom/dataAccess/roots/storeApi/carouselBanners.server';
import { useSearchParamFilter } from '@jane/bloom/hooks/filters';
import { useIsNavigatingAway } from '@jane/bloom/hooks/navigation/useIsNavigating';
import { usePinStore } from '@jane/bloom/hooks/storeSwitcher/usePinStore';
import { useMainSiteUrl } from '@jane/bloom/hooks/useMainSiteUrl';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { track } from '@jane/bloom/services/tracking/mixpanel';
import { buildCustomImageAdSlide } from '@jane/bloom/viewModels/customImageAd';

interface BloomCarouselProps {
  adIntendedDisplay?: IntendedDisplay;
  customImageAdRow?: SmartSort<MaterializedMenuProduct>;
  items: CarouselBanner;
  storeName?: string;
}

const AUTO_SLIDE_INTERVAL = 7000;

const SCROLL_POLL_INTERVAL = 50;
const SCROLL_POLL_MAX_ATTEMPTS = 20;

// First non-ad row
const FEATURED_ROW_SELECTOR = '.customRow:not(.sponsoredRow), .janeGoldRow';

const scrollTo = (element: Element) =>
  element.scrollIntoView({ behavior: 'smooth', block: 'start' });

// Scroll to featured row first if present, fallback to results
const scrollToResults = (attempt = 0) => {
  const featuredRow = document.querySelector(FEATURED_ROW_SELECTOR);
  if (featuredRow) {
    scrollTo(featuredRow);
  }

  const results = document.getElementById(MENU_RESULTS_ELEMENT_ID);
  if (results) {
    scrollTo(results);
    return;
  }

  // For edge case, where featured page has no row(s) & no results loaded yet, try again
  if (attempt < SCROLL_POLL_MAX_ATTEMPTS) {
    setTimeout(() => scrollToResults(attempt + 1), SCROLL_POLL_INTERVAL);
    return;
  }
};

const withAdSlide = (
  slides: BannerCarouselSlide[],
  adSlide: BannerCustomImageAdSlide | undefined,
  adPosition: number
): BannerCarouselSlide[] => {
  if (!adSlide) {
    return slides;
  }

  return [
    ...slides.slice(0, adPosition - 1),
    adSlide,
    ...slides.slice(adPosition - 1),
  ];
};

export const BloomCarousel = ({
  adIntendedDisplay,
  customImageAdRow,
  items,
  storeName,
}: BloomCarouselProps) => {
  const { featureFlags, pinnedStore } = useRootLoaderData();
  const secondaryPosition = !!featureFlags[FLAGS.p1PlaceImageAdSecond];
  const adPosition = secondaryPosition && items.length > 0 ? 2 : 1;
  const adBrandName = customImageAdRow?.products[0]?.attributes.brand ?? '';
  const showPrice = !!featureFlags[FLAGS.p1DisplayPriceForImageAds];

  const { pinning: manuallyPinning } = usePinStore();
  const state = pinnedStore?.state;
  const isNavigatingAway = useIsNavigatingAway();
  const { getFilterOptionValue: getBrandFilter, setFilter: setBrandFilter } =
    useSearchParamFilter('brands');
  const mainSiteUrl = useMainSiteUrl();

  const isModalOpen = useIsModalOpen();

  // context shared by the image ad's RUM actions
  const adRumContext = useMemo(
    () => ({
      brand: customImageAdRow?.products[0]?.attributes.brand,
      intendedDisplay: adIntendedDisplay,
      position: adPosition === 2 ? 'second' : 'first',
    }),
    [adIntendedDisplay, adPosition, customImageAdRow]
  );

  const onImageClick = useCallback(
    (brandName: string) => {
      datadogRum.addAction('image_ad.click', {
        ...adRumContext,
        target: 'creative',
      });

      if (brandName) {
        if (!getBrandFilter(brandName)) {
          setBrandFilter(brandName, true);
        }

        scrollToResults();
      }
    },
    [adRumContext, getBrandFilter, setBrandFilter]
  );

  const [viewedRows] = useState(() => new WeakSet<object>());

  const trackAdEvent = useCallback(
    (event: WebCustomImageAdEvent) => {
      if (event.type === 'clicked_product') {
        datadogRum.addAction('image_ad.click', {
          ...adRumContext,
          productId: event.product.product_id,
          target: 'product',
        });
      }

      if (adIntendedDisplay == null) return;

      // once per ad row, matching the SDK's own impression dedupe
      if (event.type === 'viewed_ad' && customImageAdRow) {
        if (viewedRows.has(customImageAdRow)) return;

        viewedRows.add(customImageAdRow);
      }

      track(
        imageAdTrackingPayload(event, {
          adPosition,
          brandName: adBrandName,
          intendedDisplay: adIntendedDisplay,
          menuType: 'premium',
          storeHasBanners: items.length > 0,
          storeId: pinnedStore?.id,
        })
      );
    },
    [
      adBrandName,
      adIntendedDisplay,
      adPosition,
      adRumContext,
      customImageAdRow,
      items.length,
      pinnedStore?.id,
      viewedRows,
    ]
  );

  const adSlide = useMemo(() => {
    if (customImageAdRow == null || adIntendedDisplay == null) return;

    return buildCustomImageAdSlide({
      instance: customImageAdRow,
      intendedDisplay: adIntendedDisplay,
      onEvent: trackAdEvent,
      onImageClick,
      showPrice,
      state,
    });
  }, [
    customImageAdRow,
    onImageClick,
    adIntendedDisplay,
    showPrice,
    state,
    trackAdEvent,
  ]);

  const slides = useMemo(
    () =>
      withAdSlide(
        items.map((banner) => ({
          id: String(banner.id),
          imageAlt: banner.alt_text ?? `${storeName} Marketing Banner`,
          imageSrc: banner.image_url,
          url: banner.url,
        })),
        adSlide,
        adPosition
      ),
    [adPosition, adSlide, items, storeName]
  );

  const onView = useCallback(
    (slide: BannerCarouselSlide) => {
      if (customImageAdRow == null || slide.kind !== 'customImageAd') {
        return;
      }

      datadogRum.addAction('image_ad.view', adRumContext);
      customImageAdRow.products.forEach((product) => void product.impress());
    },
    [adRumContext, customImageAdRow]
  );

  if (manuallyPinning || isNavigatingAway) {
    return <CarouselSkeleton mode="carousel" />;
  }

  if (slides.length === 0) {
    return null;
  }

  return (
    <BannerCarousel
      slides={slides}
      autoAdvanceMs={AUTO_SLIDE_INTERVAL}
      disableAutoAdvance={isModalOpen}
      onView={onView}
      comparisonUrl={mainSiteUrl}
    />
  );
};
